/**
 * Lifecycle of an {@link AuthClient}. `Starting` covers the whole ordered boot
 * sequence (credential resolution + identity fence), so consumers can hold off
 * on adopting persona/company state until it leaves that state.
 */
export const AuthStatus = {
  Idle: 'idle',
  Starting: 'starting',
  Authenticated: 'authenticated',
  Unauthenticated: 'unauthenticated',
  Failed: 'failed',
} as const;
export type AuthStatus = (typeof AuthStatus)[keyof typeof AuthStatus];

/** Where a credential came from. Both kinds drive the same state machine. */
export const CredentialSourceKind = {
  /** Interactive or silent OIDC against the identity provider. */
  Oidc: 'oidc',
  /** Handed to us by an embedding host over a verified postMessage channel. */
  Host: 'host',
} as const;
export type CredentialSourceKind =
  (typeof CredentialSourceKind)[keyof typeof CredentialSourceKind];

/**
 * Why a session ended.
 *
 * `status: 'unauthenticated'` is reached by several routes that a user-facing
 * surface must distinguish: an inactivity sign-out has to say so, a renewal
 * failure during a long task has to offer recovery rather than a logout, and a
 * provider-initiated end is nobody's fault. Without this they are one state.
 */
export const SessionEndReason = {
  /** An ordinary, deliberate sign-out. */
  SignedOut: 'signed_out',
  /** The app's inactivity policy ended it. Set by the caller, never inferred. */
  Inactivity: 'inactivity',
  /** Renewal exhausted its attempts, or the authority refused the refresh token. */
  RenewFailed: 'renew_failed',
  /** The credential expired without being renewed. */
  Expired: 'expired',
  /** The identity provider reported the session ended. */
  ProviderSignedOut: 'provider_signed_out',
  /** The stored credential belonged to a different user. */
  IdentityMismatch: 'identity_mismatch',
} as const;
export type SessionEndReason = (typeof SessionEndReason)[keyof typeof SessionEndReason];

export const AuthErrorCode = {
  /** The source could not produce a credential without user interaction. */
  RenewFailed: 'renew_failed',
  /** The authority refused the credential chain; renewal cannot recover it. */
  CredentialRejected: 'credential_rejected',
  /** The identity fence found a credential belonging to a different user. */
  IdentityMismatch: 'identity_mismatch',
  /** A host message arrived from an origin that is not on the allowlist. */
  UntrustedOrigin: 'untrusted_origin',
  /** A host token was structurally unusable (not a JWT, no exp, no sub). */
  MalformedToken: 'malformed_token',
  /** The host did not answer a token request within the configured budget. */
  HostTimeout: 'host_timeout',
  /** The operation is meaningless for the active source (e.g. signIn on host). */
  NotSupported: 'not_supported',
  /** Anything the source itself threw. */
  SourceError: 'source_error',
} as const;
export type AuthErrorCode = (typeof AuthErrorCode)[keyof typeof AuthErrorCode];

/**
 * Message types on the host handshake channel. Namespaced so a page hosting
 * several unrelated postMessage protocols cannot collide with us.
 */
export const HostMessageType = {
  /** Embeddable → host: "I am mounted, send me a token." */
  Ready: 'xemplo:auth:ready',
  /** Embeddable → host: "the token I have is expiring, send me another." */
  TokenRequest: 'xemplo:auth:token-request',
  /** Host → embeddable: here is a token. */
  TokenResponse: 'xemplo:auth:token',
  /** Host → embeddable: the session ended; tear down. */
  SignedOut: 'xemplo:auth:signed-out',
} as const;
export type HostMessageType = (typeof HostMessageType)[keyof typeof HostMessageType];

/**
 * How long before expiry a credential is treated as due for renewal. Renewing
 * early keeps a request from racing the expiry it was checked against.
 */
export const DEFAULT_RENEW_SKEW_SECONDS = 120;

/** How long to wait for a host to answer a token request before failing. */
export const DEFAULT_HOST_TIMEOUT_MS = 10_000;

/**
 * How often the renewal scheduler checks whether the credential is due.
 *
 * A repeating check rather than one long `setTimeout` per token, because a
 * `setTimeout` armed for 50 minutes does not survive a suspended laptop
 * predictably. Comparing `expiresAt` against the clock on a short interval
 * self-corrects after any wall-clock jump — the same approach `oidc-client-ts`
 * takes in its own `Timer`, and the reason its scheduling is reliable.
 */
export const DEFAULT_RENEW_CHECK_INTERVAL_MS = 5_000;

/**
 * Delays before each retry after a failed renewal.
 *
 * Four attempts spanning 50s, comfortably inside the 120s renewal window that
 * {@link DEFAULT_RENEW_SKEW_SECONDS} opens. Renewal is now a network call
 * (`POST /connect/token`), so a transient 503 or a dropped connection must not
 * end a session — one attempt per token is what makes a blip unrecoverable.
 */
export const DEFAULT_RENEW_RETRY_DELAYS_MS: readonly number[] = [5_000, 15_000, 30_000];

/**
 * Floor between two *successful* renewals.
 *
 * An access token whose lifetime is at or below the renewal skew is due for
 * renewal the moment it arrives, so the scheduler would renew on every tick.
 * That is a token-endpoint storm which — with one-time-use refresh tokens —
 * rotates the refresh token twelve times a minute. Short access-token lifetimes
 * are exactly what gets configured while testing renewal, so the guard matters
 * most precisely when someone is tuning the client.
 */
export const DEFAULT_MIN_RENEW_INTERVAL_MS = 30_000;
